class AOWQuickAdd extends HTMLElement {
  connectedCallback() {
    this.openButton = this.querySelector('[data-aow-quick-add-open]');
    this.overlay = this.querySelector('[data-aow-quick-add-overlay]');
    this.dialog = this.overlay?.querySelector('[role="dialog"]');
    this.closeButton = this.overlay?.querySelector(
      '[data-aow-quick-add-close]'
    );

    this.variantInput = this.overlay?.querySelector(
      '[data-aow-variant-input]'
    );

    this.price = this.overlay?.querySelector(
      '[data-aow-selected-price]'
    );

    this.unitPrice = this.overlay?.querySelector(
      '[data-aow-selected-unit-price]'
    );

    this.packBadge = this.overlay?.querySelector(
      '[data-aow-pack-badge]'
    );

    this.lastFocusedElement = null;

    this.originalParent = this.overlay?.parentNode || null;
    this.originalNextSibling = this.overlay?.nextSibling || null;

    this.handleOpen = this.open.bind(this);
    this.handleClose = this.close.bind(this);
    this.handleKeydown = this.onKeydown.bind(this);
    this.handleOverlayClick = this.onOverlayClick.bind(this);

    this.openButton?.addEventListener('click', this.handleOpen);
    this.closeButton?.addEventListener('click', this.handleClose);
    this.overlay?.addEventListener('click', this.handleOverlayClick);

    this.overlay
      ?.querySelectorAll('[data-aow-variant]')
      .forEach((button) => {
        button.addEventListener('click', () => {
          this.selectVariant(button);
        });
      });

    this.syncInitialVariant();
  }

  disconnectedCallback() {
    this.openButton?.removeEventListener('click', this.handleOpen);
    this.closeButton?.removeEventListener('click', this.handleClose);
    this.overlay?.removeEventListener(
      'click',
      this.handleOverlayClick
    );

    document.removeEventListener('keydown', this.handleKeydown);

    if (this.overlay?.parentNode === document.body) {
      this.overlay.remove();
    }
  }

  open() {
    if (!this.overlay) return;

    this.lastFocusedElement = document.activeElement;

    /*
      Move the popup to the document body so transformed product-card
      elements cannot trap position: fixed inside the card.
    */
    if (this.overlay.parentNode !== document.body) {
      document.body.appendChild(this.overlay);
    }

    this.overlay.hidden = false;
    this.overlay.setAttribute('aria-hidden', 'false');

    window.AOWTradePricing?.apply?.(this.overlay);

    document.body.classList.add('aow-quick-add-open');
    document.addEventListener('keydown', this.handleKeydown);

    requestAnimationFrame(() => {
      this.dialog?.focus();
    });
  }

  close() {
    if (!this.overlay) return;

    this.overlay.hidden = true;
    this.overlay.setAttribute('aria-hidden', 'true');

    document.body.classList.remove('aow-quick-add-open');
    document.removeEventListener('keydown', this.handleKeydown);

    /*
      Return the popup to its original custom element after closing.
    */
    if (
      this.originalParent &&
      this.overlay.parentNode === document.body
    ) {
      if (
        this.originalNextSibling &&
        this.originalNextSibling.parentNode === this.originalParent
      ) {
        this.originalParent.insertBefore(
          this.overlay,
          this.originalNextSibling
        );
      } else {
        this.originalParent.appendChild(this.overlay);
      }
    }

    this.lastFocusedElement?.focus();
  }

  onOverlayClick(event) {
    if (event.target === this.overlay) {
      this.close();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
    }
  }

  syncInitialVariant() {
    if (!this.overlay) return;

    const selectedVariant =
      this.overlay.querySelector(
        '[data-aow-variant].is-selected'
      ) ||
      this.overlay.querySelector(
        '[data-aow-variant]:not(.is-unavailable)'
      );

    if (selectedVariant) {
      this.selectVariant(selectedVariant, false);
    }
  }

  selectVariant(button, focusButton = true) {
    if (
      !button ||
      button.disabled ||
      button.classList.contains('is-unavailable')
    ) {
      return;
    }

    this.overlay
      ?.querySelectorAll('[data-aow-variant]')
      .forEach((variantButton) => {
        const isSelected = variantButton === button;

        variantButton.classList.toggle(
          'is-selected',
          isSelected
        );

        variantButton.setAttribute(
          'aria-checked',
          isSelected ? 'true' : 'false'
        );
      });

    if (this.variantInput) {
      this.variantInput.value =
        button.dataset.variantId || '';

      this.variantInput.dispatchEvent(
        new Event('change', {
          bubbles: true
        })
      );
    }

    const vatArea = this.overlay?.querySelector("[data-aow-quick-add-price-area]");
    if (vatArea) vatArea.dataset.aowVatTaxable = button.dataset.aowVatTaxable;
    const tradePricing = window.AOWTradePricing;
    const tradeActive = Boolean(tradePricing?.isTrade?.());

    if (this.price) {
      const cents = Number(button.dataset.variantPriceCents || 0);
      this.price.textContent =
        tradeActive && cents > 0
          ? tradePricing.formatTradeCents(cents)
          : (button.dataset.variantPrice || '');
    }

    if (this.unitPrice) {
      const unitCents = Number(button.dataset.unitPriceCents || 0);
      const unitLabel = button.dataset.unitLabel || '';
      this.unitPrice.textContent =
        tradeActive && unitCents > 0
          ? tradePricing.formatTradeCents(unitCents) + (unitLabel ? ' per ' + unitLabel : '')
          : (button.dataset.unitPrice || '');
    }

    const tradeLabel = this.overlay?.querySelector('[data-aow-quick-add-trade-label]');
    if (tradeLabel) {
      tradeLabel.hidden = !tradeActive;
    }

    if (this.packBadge) {
      const packLabel =
        button.dataset.packLabel || '';

      this.packBadge.textContent = packLabel;
      this.packBadge.hidden = !packLabel;
    }

    if (focusButton) {
      button.focus();
    }
  }
}

if (!customElements.get('aow-quick-add')) {
  customElements.define(
    'aow-quick-add',
    AOWQuickAdd
  );
}